import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, greetSr } from "@/lib/api";
import { isFirstOpenToday, markOpenedToday } from "@/lib/dailyState";
import TaskEditDialog from "@/components/TaskEditDialog";
import AIAssistant from "@/components/AIAssistant";
import KebabMenu from "@/components/KebabMenu";
import { RefreshCw, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TONE_TEXT = {
  critical: "text-terracotta",
  warning: "text-ochre",
  neutral: "text-ink-muted",
  info: "text-forest",
  success: "text-forest",
};

const TONE_BG = {
  critical: "bg-terracotta/12 border-terracotta/25 text-terracotta",
  warning: "bg-ochre/15 border-ochre/30 text-ochre-ink",
  neutral: "bg-surface-secondary border-line text-ink-muted",
  info: "bg-forest/10 border-forest/25 text-forest",
  success: "bg-forest/10 border-forest/25 text-forest",
};

function getBrowserTZ() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Belgrade";
  } catch {
    return "Europe/Belgrade";
  }
}

function initials(name) {
  return (name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0])
    .filter(Boolean)
    .join("")
    .toUpperCase();
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collabs, setCollabs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [pendingFromMe, setPendingFromMe] = useState(null);
  const [pendingOpen, setPendingOpen] = useState(false);
  const tz = useMemo(getBrowserTZ, []);
  const [showIntro] = useState(isFirstOpenToday);

  useEffect(() => {
    markOpenedToday();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [t, c, p, pm] = await Promise.all([
        api.post("/dashboard/today", { timezone: tz }),
        api.get("/collaborators"),
        api.get("/projects"),
        api.get("/dashboard/pending-from-me").catch(() => ({ data: null })),
      ]);
      setToday(t.data);
      setCollabs(c.data);
      setProjects(p.data);
      setPendingFromMe(pm.data);
    } catch {
      toast.error("Greška pri učitavanju pregleda");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      // clear cache by asking for a fresh compute — server keys on hash, so
      // we just re-request; if unchanged it will hit cache. That's OK.
      const { data } = await api.post("/dashboard/today", { timezone: tz });
      setToday(data);
    } catch {
      toast.error("Nije uspelo osvežavanje");
    }
    setLoading(false);
  };

  const openTask = (task_id) => {
    // Look up the full task and open edit dialog
    api.get(`/tasks?filter=open`).then(({ data }) => {
      const t = data.find((x) => x.id === task_id) || null;
      // Not open? try all statuses via direct GET
      if (!t) {
        api.get("/tasks").then(({ data: all }) => {
          const t2 = all.find((x) => x.id === task_id);
          if (t2) setEditing(t2);
        });
      } else {
        setEditing(t);
      }
    });
  };

  const gotoFilter = (filter) => navigate(`/taskovi?filter=${filter}`);

  const onChange = async () => { await load(); };

  const collabById = useMemo(
    () => Object.fromEntries(collabs.map((c) => [c.id, c])),
    [collabs],
  );

  const chips = today?.chips || [];
  const items = today?.items || [];
  const intro = today?.intro || "";
  const waitingOnMe = today?.waiting_on_me || null;
  const sleepingCount = today?.sleeping_count || 0;

  const waitingLine = useMemo(() => {
    if (!waitingOnMe || !waitingOnMe.shown_names?.length) return null;
    const names = waitingOnMe.shown_names;
    const extra = waitingOnMe.extra_count || 0;
    const verb = (extra > 0 || names.length > 1) ? "čekaju" : "čeka";
    let subject;
    if (names.length === 1 && extra === 0) subject = names[0];
    else if (names.length === 2 && extra === 0) subject = `${names[0]} i ${names[1]}`;
    else if (extra === 0) subject = `${names.slice(0, -1).join(", ")} i ${names[names.length - 1]}`;
    else subject = `${names.join(", ")} i još ${extra}`;
    return `${subject} ${verb} odgovor od tebe`;
  }, [waitingOnMe]);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <header>
        <h1
          className="text-3xl md:text-4xl font-heading font-semibold tracking-tighter"
          data-testid="dashboard-greeting"
        >
          {greetSr()}, {user?.name?.split(" ")[0] || ""}.
        </h1>
        {showIntro && (
          <>
            <p className="mt-2 text-sm md:text-base text-ink" data-testid="dashboard-intro">
              Ima posla ko hoće da radi. Hajde da vidimo šta je danas važno.
            </p>
            <p className="mt-1 text-xs md:text-sm text-ink-muted" data-testid="dashboard-subintro">
              Odvoji pola sata i prođi kroz ono što danas ide dalje i ono što čekaš da stigne.
            </p>
          </>
        )}

        {/* Chips row — only problems that exist */}
        {(chips.length > 0 || (pendingFromMe?.counts?.total > 0)) && (
          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="dashboard-chips">
            {chips.map((c) => (
              <button
                key={c.key}
                data-testid={`chip-${c.key}`}
                onClick={() => (c.nav ? navigate(c.nav) : gotoFilter(c.filter))}
                className={
                  "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-colors hover:opacity-90 " +
                  (TONE_BG[c.tone] || TONE_BG.neutral)
                }
              >
                <span>{c.label}</span>
                <span className="tabular-nums font-semibold">{c.value}</span>
              </button>
            ))}
            {pendingFromMe?.counts?.total > 0 && (
              <button
                data-testid="chip-pending-from-me"
                onClick={() => setPendingOpen((v) => !v)}
                className={
                  "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-colors hover:opacity-90 " +
                  TONE_BG.warning
                }
              >
                <span>Čeka tvoj odgovor</span>
                <span className="tabular-nums font-semibold">{pendingFromMe.counts.total}</span>
              </button>
            )}
          </div>
        )}

        {/* Expanded pending list (opens on chip click) */}
        {pendingOpen && pendingFromMe?.items?.length > 0 && (
          <div
            data-testid="pending-from-me-list"
            className="mt-3 border border-ochre/30 bg-ochre/6 rounded-md divide-y divide-ochre/20"
          >
            {pendingFromMe.items.map((it) => {
              const typeLabel =
                it.type === "question" ? "Pitanje" :
                it.type === "decision" ? "Odluka" : "Odobrenje";
              return (
                <div
                  key={it.comment_id}
                  data-testid={`pending-item-${it.comment_id}`}
                  className="px-3 py-2 text-xs"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded border border-ochre/30 bg-white text-ochre-ink text-[10px] uppercase tracking-wider font-semibold">
                      {typeLabel}
                    </span>
                    <span className="text-ink font-medium">{it.task_title || "Task"}</span>
                    <span className="text-ink-muted ml-auto">od {it.author_name || "korisnika"}</span>
                  </div>
                  {it.body && (
                    <div className="mt-1 text-ink-muted line-clamp-2">{it.body}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </header>

      {/* Section: Danas */}
      <section data-testid="today-section" className="surface-card overflow-hidden">
        <div className="flex items-center justify-between px-4 md:px-5 pt-4">
          <h2 className="font-heading text-base md:text-lg font-semibold">Danas</h2>
          <KebabMenu
            testid="today-kebab"
            items={[
              { label: "Osveži pregled", icon: <RefreshCw className="w-4 h-4" />, onClick: refresh, testid: "today-refresh" },
            ]}
          />
        </div>

        {/* Intro sentence */}
        <div className="px-4 md:px-5 pt-2 pb-3 min-h-[24px]">
          {loading && !today ? (
            <div className="text-sm text-ink-muted flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Pripremam pregled...
            </div>
          ) : intro ? (
            <p className="text-sm md:text-[15px] text-ink leading-snug" data-testid="today-intro">
              {intro}
            </p>
          ) : null}
        </div>

        {/* Items */}
        <ul className="divide-y divide-line border-t border-line" data-testid="today-items">
          {loading && items.length === 0 && (
            <li className="px-4 md:px-5 py-6 text-sm text-ink-muted">Učitavam...</li>
          )}
          {!loading && items.length === 0 && (
            <li className="px-4 md:px-5 py-6 text-sm text-ink-muted" data-testid="today-empty">
              Nema stavki za fokus. Uzmi vazduh.
            </li>
          )}
          {items.map((it, i) => (
            <TodayRow
              key={it.task_id}
              index={i}
              item={it}
              onClick={() => openTask(it.task_id)}
            />
          ))}
        </ul>
      </section>

      {/* Diskretna linija: ko čeka odgovor od mene (samo kada postoje podaci) */}
      {waitingLine && (
        <button
          onClick={() => gotoFilter("to_them")}
          data-testid="waiting-on-me-line"
          className="w-full text-left flex items-center justify-between gap-3 px-4 md:px-5 py-2.5 text-sm text-ink-muted hover:text-forest transition-colors group"
        >
          <span className="min-w-0 truncate">
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-ochre inline-block" aria-hidden />
              {waitingLine}
            </span>
          </span>
          <span className="text-xs text-ink-muted group-hover:text-forest whitespace-nowrap">→</span>
        </button>
      )}

      {/* Diskretna linija: zadaci koji još nisu spremna za rad (samo kada postoje) */}
      {sleepingCount > 0 && (
        <button
          onClick={() => gotoFilter("uskoro")}
          data-testid="sleeping-line"
          className="w-full text-left flex items-center justify-between gap-3 px-4 md:px-5 py-2.5 text-sm text-ink-muted hover:text-forest transition-colors group"
        >
          <span className="min-w-0 truncate">
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-sage inline-block" aria-hidden />
              {sleepingCount === 1
                ? "1 zadatak još nije spreman za rad"
                : sleepingCount >= 2 && sleepingCount <= 4
                ? `${sleepingCount} zadatka još nisu spremna za rad`
                : `${sleepingCount} zadataka još nije spremno za rad`}
            </span>
          </span>
          <span className="text-xs text-ink-muted group-hover:text-forest whitespace-nowrap">→</span>
        </button>
      )}

      {/* Compact AI input at the bottom */}
      <AIAssistant onCreated={onChange} compact collaborators={collabs} />

      <TaskEditDialog
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        task={editing}
        collaborators={collabs}
        onChanged={() => { setEditing(null); onChange(); }}
        onDeleted={() => { setEditing(null); onChange(); }}
        onSideEffect={load}
      />
    </div>
  );
}

function TodayRow({ item, index, onClick }) {
  const signal = item.signal;
  const isRelational = item.kind === "relational" && item.person_label;
  return (
    <li>
      <button
        onClick={onClick}
        data-testid={`today-item-${index}`}
        className="w-full text-left px-4 md:px-5 py-3 flex items-start gap-3 hover:bg-surface-secondary transition-colors"
      >
        {isRelational && (
          <span
            className="mt-0.5 grid place-items-center w-9 h-9 rounded-full flex-shrink-0 bg-forest/10 text-forest text-xs font-semibold"
            aria-hidden
          >
            {initials(item.person_label)}
          </span>
        )}
        <div className="min-w-0 flex-1">
          {isRelational && (
            <div className="text-xs text-ink-muted leading-tight truncate">{item.person_label}</div>
          )}
          <div className="text-sm md:text-base font-medium text-ink leading-snug break-words">
            {item.title}
          </div>
          {signal && (
            <div className="mt-1">
              <span
                data-testid={`today-signal-${index}`}
                className={
                  "inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border " +
                  (TONE_BG[signal.tone] || TONE_BG.neutral)
                }
              >
                {signal.text}
              </span>
            </div>
          )}
        </div>
      </button>
    </li>
  );
}
